// Start / end / duration triad for time entries. Any two of the three fill in
// the third, so a user can work from clock times ("9:00 to 11:30"), from a
// start plus a duration, or from an END plus a duration ("I finished at 5:00
// and worked 2.5 hours") without doing the arithmetic.
//
// ONE rule governs what gets calculated: a box that already holds a value is a
// fact, and nothing derived ever overwrites it. Concretely:
//
//   1. A BLANK box is filled from the other two — a start plus a duration fills
//      the end, an end plus a duration fills the start, two clock times fill
//      the duration.
//   2. With both clock times set, editing either one re-derives the DURATION
//      from the span, snapped to the nearest tenth of an hour.
//   3. With both clock times set, editing the duration changes the DURATION AND
//      NOTHING ELSE. The clock times stay exactly as they were entered.
//
// Clause 3 is the whole point. `hours` is snapped to a tenth of an hour, so
// deriving a clock time back out of it moves that clock by up to three minutes
// — and it fired on a bare BLUR of the duration box, which is how a start of
// 9:00 against an end of 11:28 quietly became 8:58. A duration that disagrees
// with the recorded span is legitimate (a timer row, or a billing round-up);
// where the two differ the component says so in a caption rather than
// falsifying one of them.
//
// This is the second half of the fix begun when clause 1 replaced a purely
// recency-based rule. That rule moved the END whenever an existing entry's
// start was corrected; clause 1 stopped clock edits from doing it, and clause 3
// stops duration edits from doing it. With the tie gone, so is the edit-order
// bookkeeping the old rule needed to break it.
//
// The ONE case where an already-filled clock still changes is a start dragged
// PAST its end, which leaves the span inverted and the form unsubmittable.
// That is not silently repaired either — <SpanConflictModal> asks which way the
// user wants it resolved.
//
// Derivation only ever runs in response to a user edit — never on mount. A
// timer-created row stores the real measured end, whose span won't match the
// snapped `hours`; opening a form must show what was recorded, not silently
// rewrite it.

import { useState } from 'react';
import { DateInput } from './DateInput';
import { DurationStepper } from './DurationStepper';
import { SpanConflictModal } from './SpanConflictModal';
import {
  endFromDuration,
  fmtDuration,
  hoursFromRange,
  startFromDuration,
  MS_PER_HOUR,
} from '../utils/duration';
import './TimeRangeFields.css';

/** The three fields of the triad. */
export type TimeField = 'start' | 'end' | 'hours';

const ALL_FIELDS: readonly TimeField[] = ['start', 'end', 'hours'];

/**
 * A duration box counts as filled only when it holds a real, positive number.
 * '' and '0.00' are both "nothing entered yet" — a zero-hour entry is rejected
 * by every host form anyway, so treating it as a fact would only let it block
 * the box that actually needs filling.
 */
function hasHours(h: string): boolean {
  const n = Number(h);
  return h.trim() !== '' && Number.isFinite(n) && n > 0;
}

export interface TimeRangeValue {
  /** Entry start as epoch ms. Also persisted as the entry's `date`. */
  start: number | null;
  /** Entry end as epoch ms, or null when no end was recorded. */
  end: number | null;
  /** Decimal-hours string, the NUMERIC(6,2) `hours` contract ("2.50"). */
  hours: string;
}

/**
 * A start edit that landed at or after the end. Held while the resolution
 * dialog is open; `prevStart` is what cancelling puts back.
 */
interface SpanConflict {
  start: number;
  end: number;
  hours: string;
  prevStart: number | null;
}

export interface TimeRangeFieldsProps {
  value: TimeRangeValue;
  onChange: (next: TimeRangeValue) => void;
  disabled?: boolean;
  /** Prefix for the generated input ids, so labels associate correctly. */
  idPrefix?: string;
  errors?: { start?: string; end?: string; hours?: string };
  /** Class on the outer wrapper — lets a host page reuse its own grid. */
  className?: string;
  /**
   * A flat-fee rate bills once (migration 0057), so the end time and duration
   * are optional — kept for the record, not for billing.
   */
  flatFee?: boolean;
}

export function TimeRangeFields(props: TimeRangeFieldsProps): JSX.Element {
  const {
    value, onChange, disabled = false,
    idPrefix = 'trf', errors, className, flatFee = false,
  } = props;
  const req = flatFee ? '' : ' *';
  const { start, end, hours } = value;

  const [conflict, setConflict] = useState<SpanConflict | null>(null);

  const filled: Record<TimeField, boolean> = {
    start: start !== null,
    end: end !== null,
    hours: hasHours(hours),
  };
  const blanks = ALL_FIELDS.filter((f) => !filled[f]);

  /**
   * The field the next edit will recalculate — what the "auto" chip marks.
   * Exactly one blank means that blank is next. None blank means the duration,
   * because touching either clock time re-derives it. Two blanks means there
   * isn't enough to derive anything, so nothing is marked.
   */
  const computed: TimeField | null =
    blanks.length === 1 ? blanks[0]!
      : blanks.length === 0 ? 'hours'
        : null;

  // True when the recorded span disagrees with the stored hours — a timer row,
  // clock times whose span snapping rounded, or a duration the user overrode.
  // Shown as a caption so the difference is visible rather than mysterious.
  const spanHours =
    start !== null && end !== null && end > start
      ? (end - start) / MS_PER_HOUR
      : null;
  // `hasHours` guard, not just a null check on the span: Number('') is 0, so a
  // start and end with no duration yet would read "billed at 0m" — asserting a
  // duration of zero when there simply isn't one.
  const spanDiffers =
    spanHours !== null && hasHours(hours)
    && Math.abs(spanHours - Number(hours)) >= 0.005;

  const endBeforeStart = start !== null && end !== null && end <= start;

  /**
   * The one write path. The edited field is a fact; so is every other field
   * that already holds a value. At most ONE thing gets recalculated, it is
   * never a clock time the user already filled, and only when both of its
   * inputs are present — clearing a box never cascades a null into another. An
   * orphaned value is caught by the host form's "Pick a date" check, which
   * beats silently blanking something the user did fill in.
   */
  const applyEdit = (field: TimeField, patch: Partial<TimeRangeValue>): void => {
    const next: TimeRangeValue = { start, end, hours, ...patch };

    const has: Record<TimeField, boolean> = {
      start: next.start !== null,
      end: next.end !== null,
      hours: hasHours(next.hours),
    };
    const openBoxes = ALL_FIELDS.filter((f) => f !== field && !has[f]);

    let solve: TimeField | null;
    if (openBoxes.length === 1) {
      // Clause 1 — fill the blank. This is the ONLY way a clock time is ever
      // written by the arithmetic, and only into a box that held nothing.
      solve = openBoxes[0]!;
    } else if (openBoxes.length === 0 && field !== 'hours') {
      // Clause 2 — both clock times are set and one of them just moved, so the
      // duration follows the span.
      solve = 'hours';
    } else {
      // Clause 3 — a duration edit with both clock times already set changes
      // nothing else. Also the "only one value in the whole triad" case, where
      // there is nothing to derive from yet.
      solve = null;
    }

    if (solve === 'end' && has.start && has.hours) {
      next.end = endFromDuration(next.start!, next.hours);
    } else if (solve === 'start' && has.end && has.hours) {
      next.start = startFromDuration(next.end!, next.hours);
    } else if (solve === 'hours' && has.start && has.end && next.end! > next.start!) {
      next.hours = hoursFromRange(next.start!, next.end!);
      // A backwards span is NOT turned into a duration. Where it came from a
      // start edit the dialog below sorts it out; where it came from an end
      // edit the "End time must be after the start time" message is the honest
      // answer, because rewinding a start the user entered would be worse.
    }

    // A start dragged at or past its end leaves the entry unsubmittable. The
    // start the user typed is committed either way — it is a fact like any
    // other — and the dialog asks how they want the span put right.
    if (field === 'start' && next.start !== null && next.end !== null
        && next.start >= next.end) {
      setConflict({
        start: next.start, end: next.end, hours: next.hours, prevStart: start,
      });
    }

    onChange(next);
  };

  const autoChip = (field: TimeField): JSX.Element | null =>
    (computed === field ? (
      <span
        className="trf-auto"
        title="Calculated from the other two — fill it in yourself and it stops moving"
      >
        auto
      </span>
    ) : null);

  return (
    <div className={`trf-wrap ${className ?? ''}`}>
      <div className="trf-row">
        {/* Each field is a <div> holding a `for`-associated <label>, not a
            <label> wrapping the control. A wrapping label also claims the
            stepper's +/− buttons, which would announce them as "Duration". */}
        <div className="trf-field">
          <label htmlFor={`${idPrefix}-start`}>
            Date &amp; start time *
            {autoChip('start')}
          </label>
          <DateInput
            id={`${idPrefix}-start`}
            value={start}
            onChange={(next) => applyEdit('start', { start: next })}
            withTime
            required
            disabled={disabled}
          />
          {errors?.start && <span className="field-error">{errors.start}</span>}
        </div>

        <div className="trf-field">
          <label htmlFor={`${idPrefix}-end`}>
            End time{req}
            {autoChip('end')}
          </label>
          <DateInput
            id={`${idPrefix}-end`}
            value={end}
            onChange={(next) => applyEdit('end', { end: next })}
            withTime
            disabled={disabled}
          />
          {/* Suppressed while the dialog is up: it is asking about this exact
              inversion, and the message would read as a second complaint. */}
          {endBeforeStart && conflict === null && (
            <span className="field-error">End time must be after the start time.</span>
          )}
          {errors?.end && <span className="field-error">{errors.end}</span>}
        </div>

        <div className="trf-field">
          <label htmlFor={`${idPrefix}-hours`}>
            Duration{req}
            {autoChip('hours')}
          </label>
          <DurationStepper
            id={`${idPrefix}-hours`}
            value={hours}
            onChange={(next) => applyEdit('hours', { hours: next })}
            disabled={disabled}
          />
          {spanDiffers && !endBeforeStart && !flatFee && (
            <span className="trf-hint">
              Recorded span {fmtDuration(spanHours!)} — billed at {fmtDuration(hours)}
            </span>
          )}
          {errors?.hours && <span className="field-error">{errors.hours}</span>}
        </div>
      </div>

      {flatFee ? (
        <p className="trf-legend">
          A flat fee is billed once at the amount above, so the end time and
          duration are optional — fill them in only to keep a record of the work.
        </p>
      ) : (
        <p className="trf-legend">
          Fill in any two and the third is calculated — a box you&apos;ve already
          filled is left alone. With both clock times set, the duration follows
          them, rounded to the nearest tenth of an hour; changing the duration
          after that adjusts what you bill without moving either clock time.
        </p>
      )}

      {conflict !== null && (
        <SpanConflictModal
          start={conflict.start}
          end={conflict.end}
          hours={conflict.hours}
          prevStart={conflict.prevStart}
          onResolve={(next) => {
            setConflict(null);
            onChange(next);
          }}
          onCancel={() => {
            setConflict(null);
            onChange({ ...value, start: conflict.prevStart });
          }}
        />
      )}
    </div>
  );
}
